import React, { useEffect, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { IoChevronDown, IoChevronForward, IoChevronUp, IoRadioButtonOff, IoRadioButtonOn } from "react-icons/io5";
import { TRAITS, hasTrait } from "../../../constants/devices";
import { sendControl } from "../../../services/deviceControl";
import { UserService } from "../../../services/UserService";
import { User } from "../../../storage/store";
import { useToggleTop } from "../../../components/common/ToggleTop";
import { SETTINGS_PATHS } from "./paths";
import {
  AlertDialog,
  DEFAULT_ERROR,
  SettingsPage,
  SettingsSkeleton,
  Unavailable,
  UpdateButton,
  settingsPayload,
  useAlive,
} from "./shared";
import "../../../assets/styles/app-settings.css";

// What the device does when power comes back (trait 108); the last state
// reports it as lsr.
const LSR_OPTIONS = [
  { value: "0", label: "Always OFF" },
  { value: "1", label: "Always ON" },
  { value: "2", label: "Remember Last Status" },
];

const readLsr = async (device) => {
  const lastState = await UserService.getLastStatebydeviceId(User.getPropertyId(), device.deviceId);
  return lastState?.lsr === undefined || lastState?.lsr === null ? null : String(lastState.lsr);
};

// Settings for devices with trait 106 (app/screens/DeviceSettings/
// CommonSettings.js): Last State Retention with trait 108, and the way to
// Power Settings with trait 107.
function CommonSettings() {
  const { state } = useLocation();
  const history = useHistory();
  const toast = useToggleTop();
  const alive = useAlive();
  const device = state?.data;
  const hasLsr = hasTrait(device, TRAITS.LAST_STATE);
  const hasPower = hasTrait(device, TRAITS.POWER_SETTINGS);
  // saved is what the device reports; lsr is what is picked on the page.
  const [saved, setSaved] = useState(null);
  const [lsr, setLsr] = useState(null);
  const [status, setStatus] = useState("loading");
  const [attempt, setAttempt] = useState(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!device || !hasLsr) return undefined;
    let current = true;
    setStatus("loading");
    readLsr(device)
      .then((value) => {
        if (!current) return;
        setSaved(value);
        setLsr(value);
        setStatus("ready");
      })
      .catch(() => {
        if (current) setStatus("error");
      });
    return () => {
      current = false;
    };
  }, [device, hasLsr, attempt]);

  if (!device) return <Redirect to="/dashboard" />;

  const changed = lsr !== null && lsr !== saved;
  const picked = LSR_OPTIONS.find((option) => option.value === lsr);

  // Sends the choice, then reads the last state back. If the device still
  // reports the old value, the command didn't reach it (CommonSettings.js
  // onUpdate).
  const update = async () => {
    if (busy || !changed) return;
    const previous = saved;
    setBusy("Updating…");
    try {
      await sendControl(settingsPayload(device, { id: 1, trait: Number(TRAITS.LAST_STATE), value: Number(lsr) }));
    } catch (error) {
      if (alive.current) setBusy(null);
      toast(DEFAULT_ERROR, "red");
      return;
    }
    try {
      const reported = await readLsr(device);
      if (!alive.current) return;
      setLsr(reported);
      if (reported === previous) setFailed(true);
      else setSaved(reported);
    } catch (error) {
      // Couldn't confirm it: keep the choice so Update can be tried again.
      if (alive.current) setFailed(true);
    } finally {
      if (alive.current) setBusy(null);
    }
  };

  return (
    <SettingsPage
      title="Settings"
      device={device}
      busy={busy}
      headerRight={changed ? <UpdateButton onClick={update} busy={Boolean(busy)} /> : null}
    >
      {!hasLsr && !hasPower ? <Unavailable /> : null}

      {hasLsr && status === "loading" ? <SettingsSkeleton /> : null}

      {hasLsr && status === "error" ? (
        <div className="hx-empty hx-set-empty" role="alert">
          Couldn't read this device's settings.
          <button type="button" className="hx-btn" onClick={() => setAttempt(attempt + 1)}>
            Try again
          </button>
        </div>
      ) : null}

      {hasLsr && status === "ready" ? (
        <section className="hx-set-card">
          <button
            type="button"
            className="hx-set-row"
            aria-expanded={open}
            aria-controls="hx-set-lsr"
            onClick={() => setOpen(!open)}
          >
            <span className="hx-set-row-text">
              Last State Retention
              {picked ? <span className="hx-set-row-sub">{picked.label}</span> : null}
            </span>
            {open ? <IoChevronUp aria-hidden="true" /> : <IoChevronDown aria-hidden="true" />}
          </button>
          <div
            id="hx-set-lsr"
            className="hx-set-options"
            role="radiogroup"
            aria-label="Last State Retention"
            hidden={!open}
          >
            {LSR_OPTIONS.map((option) => {
              const checked = lsr === option.value;
              const Icon = checked ? IoRadioButtonOn : IoRadioButtonOff;
              return (
                <label key={option.value} className={`hx-set-option ${checked ? "is-checked" : ""}`}>
                  {option.label}
                  <input
                    type="radio"
                    name="hx-set-lsr"
                    value={option.value}
                    checked={checked}
                    onChange={() => setLsr(option.value)}
                  />
                  <Icon className="hx-set-option-icon" aria-hidden="true" />
                </label>
              );
            })}
          </div>
        </section>
      ) : null}

      {hasPower ? (
        <section className="hx-set-card">
          <button
            type="button"
            className="hx-set-row"
            onClick={() => history.push(SETTINGS_PATHS.POWER, { data: device })}
          >
            <span className="hx-set-row-text">Power Settings</span>
            <IoChevronForward aria-hidden="true" />
          </button>
        </section>
      ) : null}

      <AlertDialog
        open={failed}
        title="Network error!"
        message="Please check your network connection or device connectivity"
        onClose={() => setFailed(false)}
      />
    </SettingsPage>
  );
}

export default CommonSettings;
